import React, { forwardRef, useState } from 'react';
import { motion } from 'motion/react';
import twMerge from 'utilities/src/twMerge';
import Image from '../Image';
import getAiUrl from './utils/getAiUrl';
import { RING_MASK_CLASSES, type AiDestination } from './constants';

interface Props {
  destination: AiDestination;
  prompt: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

const chipRestTransform = { scale: 1, rotate: 0 };
const chipPopTransform = { scale: [1, 1.25, 1], rotate: [0, -6, 0] };

const OptionRow = forwardRef<HTMLAnchorElement, Props>(({ destination, prompt, onClick }, ref) => {
  const { label, description, logo, baseUrl, queryKey } = destination;
  const [isLaunching, setIsLaunching] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setIsLaunching(true);
    onClick?.(e);
  };

  const handleFlashComplete = () => {
    if (isLaunching) setIsLaunching(false);
  };

  return (
    <motion.li variants={rowVariants} transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}>
      {/* eslint-disable-next-line react/jsx-no-target-blank */}
      <a
        ref={ref}
        href={getAiUrl(baseUrl, queryKey, prompt)}
        target="_blank"
        rel="noopener"
        aria-label={`Ask ${label} to summarize this page (opens in a new tab)`}
        onClick={handleClick}
        className="group/row shadow-lv3 bg-site-neutrals-0 text-site-neutrals-900 relative flex min-h-[3.625rem] items-center gap-2.5 overflow-visible rounded-lg p-2.5 outline-none transition-shadow duration-300 ease-out hover:shadow-[0_0_0_1px_theme(colors.site.primary.600/35%),0_0_24px_-2px_theme(colors.site.primary.600/55%),0px_4px_8px_-2px_theme(colors.site.neutrals.900/10%),0px_2px_4px_-2px_theme(colors.site.neutrals.900/10%)] focus-visible:shadow-[0px_4px_8px_-2px_theme(colors.site.neutrals.900/10%),0px_2px_4px_-2px_theme(colors.site.neutrals.900/10%),0_0_0_3px_theme(colors.site.primary.600/35%)]"
      >
        {/* static mask defines the border shape; only the oversized gradient child rotates */}
        <span
          aria-hidden
          className={twMerge(
            'pointer-events-none absolute -inset-px z-[3] overflow-hidden rounded-[9px] opacity-0 transition-opacity duration-300 ease-out group-hover/row:opacity-100 group-focus-visible/row:opacity-100',
            RING_MASK_CLASSES,
          )}
        >
          <span className="group-hover/row:animate-orbit group-focus-visible/row:animate-orbit absolute left-1/2 top-1/2 aspect-square w-[200%] -translate-x-1/2 -translate-y-1/2 bg-[conic-gradient(from_0deg,theme(colors.site.neutrals.0)_0deg,theme(colors.site.primary.600)_40deg,theme(colors.site.primary.200)_100deg,theme(colors.site.primary.200/30%)_165deg,theme(colors.site.primary.200/30%)_195deg,theme(colors.site.primary.200)_260deg,theme(colors.site.primary.600)_320deg,theme(colors.site.neutrals.0)_360deg)]" />
        </span>
        {isLaunching && (
          <>
            <motion.span
              aria-hidden
              className={twMerge(
                'from-site-primary-200 to-site-primary-600 pointer-events-none absolute -inset-px z-[3] rounded-lg bg-[linear-gradient(135deg,var(--tw-gradient-stops))] drop-shadow-[0_0_5px_theme(colors.site.primary.600/70%)]',
                RING_MASK_CLASSES,
              )}
              initial={{ opacity: 1 }}
              animate={{ opacity: 0 }}
              transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
              onAnimationComplete={handleFlashComplete}
            />

            <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg">
              <motion.span
                className="via-site-primary-600/[0.14] absolute inset-y-0 left-0 w-3/5 bg-gradient-to-r from-transparent to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '250%' }}
                transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
              />
            </span>
          </>
        )}
        <motion.span
          className="bg-site-neutrals-0 relative z-[2] flex size-[2.375rem] shrink-0 items-center justify-center rounded"
          whileHover={{ scale: 1.05 }}
          animate={isLaunching ? chipPopTransform : chipRestTransform}
          transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <Image src={logo} alt="" width={24} height={24} className="max-w-none shrink-0" />
        </motion.span>
        <span className="relative z-[2] flex min-w-0 flex-1 flex-col justify-center">
          <span className="site-paragraph-s text-site-neutrals-900 truncate">Open in{` ${label}`}</span>
          <span className="text-site-neutrals-400 truncate text-xs leading-[1.125rem]">{description}</span>
        </span>
      </a>
    </motion.li>
  );
});

OptionRow.displayName = 'AiSummaryWidgetOptionRow';

export default OptionRow;
